// A case's dashboard (PRD-0075 R-D2): the file over time, computed on read from the case's
// change log and Monitor's alerts. Months are `YYYY-MM` in UTC.
import type { AlertDto, StandingSourceDto } from './case-monitor'
import { apiClient } from './client'

/** The kinds of source the monthly chart stacks, bottom first (`SourceKind` on the server). */
export const SOURCE_KINDS = ['document', 'table', 'chat', 'recording', 'humint', 'osint'] as const

export type SourceKind = (typeof SOURCE_KINDS)[number]

/** A headline count with its 12-month sparkline (`DashboardTileOut`). */
export interface DashboardTileDto {
  total: number
  last_30_days: number
  /** One count per month of `window`, oldest first. */
  sparkline: number[]
}

/** One month of the chart: sources learned by kind (every kind, zero included) and alerts raised. */
export interface SourceMonthDto {
  month: string
  sources: Record<SourceKind, number>
  alerts: number
}

/** Where a hypothesis's last two verdicts point: towards supported, towards refuted, or neither. */
export type HypothesisTrend = 'up' | 'down' | 'steady'

/** An open hypothesis as it stood (`HypothesisStandingOut`); null verdict before any assessment. */
export interface HypothesisStandingDto {
  id: string
  title: string
  verdict: string | null
  confidence: string | null
  assessed_at: string | null
  previous_verdict: string | null
  /** Null until there are two assessments. */
  trend: HypothesisTrend | null
}

/**
 * The newest judgment in words (`AssessmentNoteOut`): an assessment's rationale (with its verdict) or
 * an investigation's assessment note (with its investigation, and the hypothesis it is evidence for).
 */
export interface AssessmentNoteDto {
  text: string
  author_name: string
  written_at: string
  hypothesis_id: string | null
  hypothesis_title: string | null
  investigation_id: string | null
  investigation_title: string | null
  verdict: string | null
  confidence: string | null
}

/** A watched entity and when the case last learned a mention of it; null when it had not. */
export interface WatchedObjectDto {
  uid: string
  label: string | null
  entity_type: string | null
  last_seen_at: string | null
}

/** The lists beside the chart, each as of the dashboard's date (`DashboardPanelsOut`). */
export interface DashboardPanelsDto {
  hypotheses: HypothesisStandingDto[]
  latest_note: AssessmentNoteDto | null
  /** The top of the inbox: the newest learned alerts, whatever their triage state. */
  whats_new: AlertDto[]
  /** The one seen most recently first, those never seen last. */
  watched: WatchedObjectDto[]
  /** The latest intake first. */
  sources: StandingSourceDto[]
}

/** `GET /v1/cases/{case_id}/dashboard` (`CaseDashboardResponse`). */
export interface CaseDashboardDto {
  case_id: string
  name: string
  /** The earlier of the case's creation and the first source it learned; ISO 8601. */
  opened_at: string
  /** When the case last learned a source; null before the first one. */
  last_intake_at: string | null
  as_of: string
  /** The twelve months the sparklines cover, oldest first. */
  window: string[]
  tiles: {
    sources: DashboardTileDto
    new_objects: DashboardTileDto
    alerts: DashboardTileDto
  }
  /** From the first month the file learned anything (or the window's start) to the month of `as_of`. */
  months: SourceMonthDto[]
  panels: DashboardPanelsDto
}

export const caseDashboardApi = {
  /** As of now, or with `asOf` (*Spis k dátumu*, an ISO time) as the case had learned it by then. */
  get: (caseId: string, options: { asOf?: string; signal?: AbortSignal } = {}) =>
    apiClient.get<CaseDashboardDto>(`/v1/cases/${caseId}/dashboard`, {
      params: { as_of: options.asOf },
      signal: options.signal,
    }),
}
